/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
// hooks/useSubscriptions.ts
import { useState, useEffect, useCallback } from "react";
import { fetchAllAlertSubscriptions, fetchCustomAlerts } from "../requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import {
  AlertTemplate,
  CustomAlertDetails,
  Notification,
  Subscription,
} from "../NotificationTypes";

export function useSubscriptions(
  type: string,
  customerId: string | null,
  serviceId: string | null,
  templates: AlertTemplate[],
  subscriberType: "user" | "tenant",
  subscriberId: string
) {
  const [subs, setSubs] = useState<Notification[]>([]);
  const [loadingSet, setLoadingSet] = useState<Set<string>>(new Set());
  const api = useAxiosAuth0();

  const load = useCallback(async () => {
    if (!customerId || !serviceId) {
      setSubs([]);
      return;
    }
    setLoadingSet((s) => new Set(s).add(serviceId));

    try {
      //Load the base subscriptions
      const resp = await fetchAllAlertSubscriptions(
        api,
        subscriberType,
        subscriberId
      );

      const rawSubs: any[] = resp.data.data;
      const relevant = rawSubs.filter(
        (s) =>
          s.serviceType === type &&
          String(s.serviceId) === serviceId
      );


      //Filter for this service
      if (relevant.length === 0) {
        setSubs([]);
        return;
      }

      //Fetch custom alerts for this subscriber
      const customResp = await fetchCustomAlerts(
        api,
        subscriberType,
        subscriberId
      );
      const customData = customResp.data.data;
      // normalize into an array, whatever shape it comes back in:
      const customsArray: Array<(typeof customData)[0]> = Array.isArray(
        customData
      )
        ? customData
        : //It's wrapped in another property?
        Array.isArray((customData as any).customAlerts)
        ? (customData as any).customAlerts
        : [];

      //Build the map
      const customMap = new Map<string, (typeof customsArray)[0]>();
      customsArray.forEach((c) => {
        // originalAlertId
        const origId = (c as any).originalAlertId ?? (c as any).id;
        customMap.set(origId, c);
      });

      //For each subscription, pick custom if exists
      const mapped: Notification[] = relevant.map((s) => {
        const override = customMap.get(s.alertId);
        const effectiveAlert = override ?? s.alert;

        // subscription unchanged
        const subscription: Subscription = {
          id: s.id,
          alertId: s.alertId,
          templateId: s.alert.templateId,
          subscriberType: s.subscriberType,
          subscriberId: s.subscriberId,
          serviceType: s.serviceType,
          serviceId: s.serviceId,
          enabled: s.enabled,
          subscriberName: s.subscriberName,
          conditions: Array.isArray(effectiveAlert.conditions)
            ? effectiveAlert.conditions.map((c: any) => ({
                type: c.type,
                operator: c.operator,
                value: c.value,
              }))
            : [],
          interval: effectiveAlert.interval ?? "",
        };

        const firstCond = subscription.conditions?.[0] ?? {
          operator: "",
          value: 0,
        };
        const customAlert: CustomAlertDetails = {
          templateId: effectiveAlert.templateId,
          id: effectiveAlert.id,
          name: effectiveAlert.name,
          description: effectiveAlert.description,
          severity: effectiveAlert.severity ?? "",
          interval: effectiveAlert.interval ?? "",
          conditions: {
            operator: firstCond.operator,
            threshold: firstCond.value,
          },
          isCustomized: Boolean(override), // true if we used a custom override
        };

        return {
          id: s.id,
          serviceId: String(s.serviceId),
          notificationId: s.alertId,
          subscriberId: s.subscriberId,
          subscriptionId: s.id,
          subscriberName: s.subscriberName,
          enabled: s.enabled,
          recipients: (s.notificationChannels ?? [])
          .filter((c: any) => c.channelType === "email" && c.enabled)
          .flatMap((c: any) => c.channelConfig.emails),        
          subscription,
          customAlert,
          templateId: s.alert.templateId,
          isCustomized: Boolean(override),
          notificationChannels: s.notificationChannels,
        };
      });

      setSubs(mapped);
    } catch (err) {
      console.error("Failed to load subscriptions", err);
    } finally {
      setLoadingSet((s) => {
        s.delete(serviceId);
        return new Set(s);
      });
    }
  }, [api, type, customerId, serviceId, templates, subscriberType, subscriberId ]);

  useEffect(() => {
    load();
  }, [load]);

  return { subs, loadingSet, reload: load };
}
